import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Code2, Eye } from 'lucide-react';

/** Valores de exemplo para pré-visualização local (substitui {{ variavel }}). */
export const EMAIL_TEMPLATE_SAMPLE_VALUES = {
  user_name: 'Maria Silva',
  user_email: 'maria@example.com',
  org_name: 'SEMACE',
  login_url: 'https://useambiental.com.br/login',
  reset_url: 'https://useambiental.com.br/reset-password?token=exemplo',
  setup_url: 'https://useambiental.com.br/setup-password?token=exemplo',
  expires_minutes: '60',
  licenca_numero: 'LO-2026-00412',
  empresa: 'Indústria Verde Ltda',
  data: '15/09/2026',
  portal_url: 'https://useambiental.com.br',
  payment_url: 'https://useambiental.com.br/assinatura/pagar',
  amount: '890,00',
  subject: 'Comunicado ambiental',
  message_html: 'Conteúdo da mensagem aparece aqui.',
  message_text: 'Conteúdo da mensagem aparece aqui.',
};

const EMAIL_ASSETS = `${typeof window !== 'undefined' ? window.location.origin : ''}/brand/email`;

/** Espelha o layout aplicado no envio (notification-service/app/services/email_layout.py). */
function wrapLocalEmailPreview(innerHtml, bannerTitle = 'useAmbiental') {
  const trimmed = String(innerHtml || '').trim();
  if (!trimmed) return '';
  if (/^<!doctype html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed)) return trimmed;
  return `<!DOCTYPE html>
<html lang="pt-BR">
<body style="margin:0;padding:0;background:#F0F7F2;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#F0F7F2;">
    <tr><td align="center" style="padding:32px 12px;">
      <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="max-width:600px;width:100%;background:#fff;border-radius:18px;overflow:hidden;">
        <tr><td bgcolor="#14532D" style="line-height:0;font-size:0;"><img src="${EMAIL_ASSETS}/header.png" width="600" height="180" alt="useAmbiental" style="display:block;width:100%;max-width:600px;height:auto;border:0;"></td></tr>
        <tr><td align="center" style="padding:30px 32px 6px;">
          <p style="margin:0 0 12px;font-size:24px;font-weight:700;color:#14532D;line-height:1.3;letter-spacing:-0.02em;">${bannerTitle}</p>
          <table role="presentation" cellspacing="0" cellpadding="0"><tr><td style="width:56px;height:4px;background:#0D9488;border-radius:2px;line-height:0;font-size:0;">&nbsp;</td></tr></table>
        </td></tr>
        <tr><td style="padding:22px 32px 0;">${trimmed}</td></tr>
        <tr><td align="center" style="padding:34px 32px 0;">
          <p style="margin:0 0 10px;font-size:19px;font-weight:700;color:#14532D;">Receba alertas de licenças no app</p>
          <p style="margin:0 0 20px;font-size:14px;line-height:1.65;color:#3F5A4A;">Vencimentos, condicionantes e atualizações de processo na palma da mão, sem precisar ligar para o órgão ou vasculhar e-mail.</p>
          <table role="presentation" cellspacing="0" cellpadding="0" style="margin:0 auto;"><tr>
            <td style="padding:0 7px 0 0;"><img src="${EMAIL_ASSETS}/badge-app-store.png" width="180" height="56" alt="Baixar na App Store" style="display:block;width:180px;height:56px;border:0;"></td>
            <td style="padding:0 0 0 7px;"><img src="${EMAIL_ASSETS}/badge-google-play.png" width="180" height="56" alt="Disponível no Google Play" style="display:block;width:180px;height:56px;border:0;"></td>
          </tr></table>
        </td></tr>
        <tr><td align="center" style="padding:32px 32px 24px;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-top:1px solid #D1E7DD;"><tr><td style="height:22px;font-size:0;">&nbsp;</td></tr></table>
          <p style="margin:0 0 12px;font-size:12px;line-height:1.7;color:#7A9A88;">Nos ajude a melhorar nossa comunicação. Escolha quais notificações você quer receber.</p>
          <p style="margin:0 0 12px;font-size:12px;line-height:1.7;color:#7A9A88;">Ajude outros órgãos e empresas a simplificarem o licenciamento: compartilhe o useAmbiental.</p>
          <p style="margin:0 0 18px;font-size:12px;line-height:1.7;color:#7A9A88;">Ficou com dúvida? Visite a central de ajuda ou fale com a equipe responsável pelo processo.</p>
          <table role="presentation" cellspacing="0" cellpadding="0" style="margin:4px auto 0;"><tr>
            <td style="padding:0 6px;"><img src="${EMAIL_ASSETS}/social-instagram.png" width="40" height="40" alt="Instagram" style="display:block;width:40px;height:40px;border:0;"></td>
            <td style="padding:0 6px;"><img src="${EMAIL_ASSETS}/social-youtube.png" width="40" height="40" alt="YouTube" style="display:block;width:40px;height:40px;border:0;"></td>
          </tr></table>
        </td></tr>
        <tr><td align="center" bgcolor="#052E16" style="background-color:#052E16;padding:20px 32px;">
          <p style="margin:0 0 4px;font-size:13px;font-weight:700;color:#fff;">useAmbiental</p>
          <p style="margin:0;font-size:11px;line-height:1.7;color:rgba(255,255,255,0.62);">Simplificamos o licenciamento ambiental com clareza e rastreabilidade<br>Mensagem automática — por favor, não responda a este e-mail.</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body></html>`;
}

export function interpolateEmailTemplate(html, extraVariables = {}) {
  if (!html || !String(html).trim()) return '';
  const vars = { ...EMAIL_TEMPLATE_SAMPLE_VALUES, ...extraVariables };
  return String(html)
    .replace(/\{%\s*endif\s*%\}/g, '')
    .replace(/\{%\s*if\s+[^%]+%\}/g, '')
    .replace(/\{\{\s*([^}]+)\s*\}\}/g, (_, expr) => {
      const key = String(expr).split(/\s+or\s+/)[0].trim();
      if (vars[key] != null && vars[key] !== '') return String(vars[key]);
      return `[${key}]`;
    });
}

export function renderEmailTemplatePreview(html, extraVariables = {}, bannerTitle) {
  const interpolated = interpolateEmailTemplate(html, extraVariables);
  if (!interpolated) return '';
  return wrapLocalEmailPreview(
    interpolated,
    bannerTitle || extraVariables.email_banner_title || 'useAmbiental',
  );
}

function insertIntoString(value, start, end, text) {
  const before = value.slice(0, start);
  const after = value.slice(end);
  return {
    next: before + text + after,
    cursor: start + text.length,
  };
}

export function EmailTemplateEditor({
  bodyHtml,
  onBodyHtmlChange,
  subject = '',
  onSubjectChange,
  variables = [],
  onInsertVariable,
  serverPreviewHtml = null,
  serverPreviewSubject = null,
  minEditorHeight = '420px',
}) {
  const textareaRef = useRef(null);
  const subjectRef = useRef(null);
  const htmlSelectionRef = useRef({ start: 0, end: 0, scrollTop: 0 });
  const subjectSelectionRef = useRef({ start: 0, end: 0 });
  const pendingHtmlCaretRef = useRef(null);
  const pendingSubjectCaretRef = useRef(null);
  const skipExternalSyncRef = useRef(false);

  const [mode, setMode] = useState('split');
  const [draftHtml, setDraftHtml] = useState(bodyHtml || '');
  const [localPreview, setLocalPreview] = useState('');

  const sampleVars = useMemo(
    () => Object.fromEntries(variables.map((v) => [v, EMAIL_TEMPLATE_SAMPLE_VALUES[v] ?? `[${v}]`])),
    [variables],
  );

  useEffect(() => {
    if (skipExternalSyncRef.current) {
      skipExternalSyncRef.current = false;
      return;
    }
    setDraftHtml(bodyHtml || '');
  }, [bodyHtml]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setLocalPreview(renderEmailTemplatePreview(draftHtml, sampleVars));
    }, 280);
    return () => window.clearTimeout(t);
  }, [draftHtml, sampleVars]);

  useLayoutEffect(() => {
    const ta = textareaRef.current;
    const pending = pendingHtmlCaretRef.current;
    if (ta && pending) {
      ta.focus({ preventScroll: true });
      ta.setSelectionRange(pending.cursor, pending.cursor);
      ta.scrollTop = pending.scrollTop;
      pendingHtmlCaretRef.current = null;
    }
  }, [draftHtml]);

  useLayoutEffect(() => {
    const input = subjectRef.current;
    const pending = pendingSubjectCaretRef.current;
    if (input && pending) {
      input.focus({ preventScroll: true });
      input.setSelectionRange(pending.cursor, pending.cursor);
      pendingSubjectCaretRef.current = null;
    }
  }, [subject]);

  const saveHtmlSelection = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    htmlSelectionRef.current = {
      start: ta.selectionStart,
      end: ta.selectionEnd,
      scrollTop: ta.scrollTop,
    };
  }, []);

  const saveSubjectSelection = useCallback(() => {
    const input = subjectRef.current;
    if (!input) return;
    subjectSelectionRef.current = {
      start: input.selectionStart,
      end: input.selectionEnd,
    };
  }, []);

  const emitHtmlChange = useCallback(
    (next, caret, scrollTop) => {
      setDraftHtml(next);
      skipExternalSyncRef.current = true;
      onBodyHtmlChange(next);
      if (caret != null) {
        pendingHtmlCaretRef.current = { cursor: caret, scrollTop: scrollTop ?? 0 };
      }
    },
    [onBodyHtmlChange],
  );

  const handleHtmlChange = useCallback(
    (e) => {
      saveHtmlSelection();
      const next = e.target.value;
      setDraftHtml(next);
      skipExternalSyncRef.current = true;
      onBodyHtmlChange(next);
    },
    [onBodyHtmlChange, saveHtmlSelection],
  );

  const handleInsertVar = useCallback(
    (varName, target = 'body') => {
      const token = `{{ ${varName} }}`;

      if (target === 'subject' && onSubjectChange) {
        const input = subjectRef.current;
        const { start, end } =
          input && document.activeElement === input
            ? { start: input.selectionStart, end: input.selectionEnd }
            : subjectSelectionRef.current;
        const { next, cursor } = insertIntoString(subject || '', start, end, token);
        pendingSubjectCaretRef.current = { cursor };
        onSubjectChange(next);
        onInsertVariable?.(varName, 'subject');
        return;
      }

      const ta = textareaRef.current;
      const { start, end, scrollTop } =
        ta && document.activeElement === ta
          ? {
              start: ta.selectionStart,
              end: ta.selectionEnd,
              scrollTop: ta.scrollTop,
            }
          : htmlSelectionRef.current;

      const { next, cursor } = insertIntoString(draftHtml || '', start, end, token);
      emitHtmlChange(next, cursor, scrollTop);
      onInsertVariable?.(varName, 'body');
    },
    [draftHtml, emitHtmlChange, onInsertVariable, onSubjectChange, subject],
  );

  const onVarMouseDown = useCallback(
    (e, varName, target) => {
      e.preventDefault();
      handleInsertVar(varName, target);
    },
    [handleInsertVar],
  );

  const previewHtml = serverPreviewHtml ?? localPreview;
  const previewSubject = serverPreviewSubject ?? interpolateEmailTemplate(subject, sampleVars);

  const showCode = mode === 'code' || mode === 'split';
  const showPreview = mode === 'preview' || mode === 'split';

  return (
    <div className="email-template-editor space-y-3">
      {onSubjectChange && (
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Assunto</label>
          <input
            ref={subjectRef}
            type="text"
            className="form-input font-mono text-sm w-full"
            placeholder="Recuperação de senha — {{ org_name }}"
            value={subject || ''}
            onChange={(e) => onSubjectChange(e.target.value)}
            onSelect={saveSubjectSelection}
            onKeyUp={saveSubjectSelection}
            onClick={saveSubjectSelection}
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg ${mode === 'split' ? 'bg-primary-100 text-primary-800 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
          onClick={() => setMode('split')}
        >
          <Code2 className="w-4 h-4" />
          Código + visual
        </button>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg ${mode === 'code' ? 'bg-primary-100 text-primary-800 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
          onClick={() => setMode('code')}
        >
          <Code2 className="w-4 h-4" />
          Só HTML
        </button>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg ${mode === 'preview' ? 'bg-primary-100 text-primary-800 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
          onClick={() => setMode('preview')}
        >
          <Eye className="w-4 h-4" />
          Só visual
        </button>
      </div>

      <div
        className={`grid gap-4 ${mode === 'split' ? 'lg:grid-cols-2' : 'grid-cols-1'}`}
        style={{ minHeight: minEditorHeight }}
      >
        {showCode && (
          <div className="flex flex-col min-h-0 order-1">
            <label className="text-xs font-medium text-gray-600 mb-1">Código HTML</label>
            <textarea
              ref={textareaRef}
              className="form-input font-mono text-xs leading-5 flex-1 resize-y w-full"
              style={{ minHeight: minEditorHeight, tabSize: 2 }}
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              wrap="off"
              value={draftHtml}
              onChange={handleHtmlChange}
              onSelect={saveHtmlSelection}
              onKeyUp={saveHtmlSelection}
              onClick={saveHtmlSelection}
              onScroll={saveHtmlSelection}
              placeholder="<!-- email-layout:v2 -->&#10;<p>Olá, {{ user_name }}</p>"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Posicione o cursor no HTML e clique na variável abaixo para inserir na linha atual.
            </p>

            {variables.length > 0 && (
              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-600 mb-2">Variáveis (insere no cursor)</p>
                <div className="flex flex-wrap gap-2">
                  {variables.map((v) => (
                    <div key={v} className="flex gap-1">
                      <button
                        type="button"
                        title="Inserir no corpo HTML na posição do cursor"
                        className="text-xs px-2 py-1 rounded bg-white border border-gray-200 hover:bg-primary-50 hover:border-primary-300 font-mono"
                        onMouseDown={(e) => onVarMouseDown(e, v, 'body')}
                      >
                        {`{{ ${v} }}`}
                      </button>
                      {onSubjectChange && (
                        <button
                          type="button"
                          title="Inserir no assunto na posição do cursor"
                          className="text-[10px] px-1.5 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
                          onMouseDown={(e) => onVarMouseDown(e, v, 'subject')}
                        >
                          + assunto
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {showPreview && (
          <div className="flex flex-col min-h-0 order-2">
            <label className="text-xs font-medium text-gray-600 mb-1">
              Visualização {serverPreviewHtml ? '(servidor)' : '(exemplo local)'}
            </label>
            {previewSubject ? (
              <div className="text-sm font-semibold text-gray-800 mb-2 px-1 truncate" title={previewSubject}>
                Assunto: {previewSubject}
              </div>
            ) : null}
            <div
              className="flex-1 rounded-lg border border-gray-200 bg-gray-100 overflow-hidden"
              style={{ minHeight: minEditorHeight }}
            >
              {previewHtml ? (
                <iframe
                  title="Pré-visualização do e-mail"
                  sandbox=""
                  className="w-full h-full bg-white"
                  style={{ minHeight: minEditorHeight, border: 0 }}
                  srcDoc={previewHtml}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-gray-500 p-4">
                  Cole HTML à esquerda para ver a prévia renderizada.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmailTemplateEditor;
